/**
 * wl_jobs — wireline & slickline. GET /dashboard/wl-jobs (api#1362, WL-6):
 * open jobs, in progress first, with their units, day sheets awaiting the
 * office, pressure-control attention, units without a stack and late starts.
 * "Late", "attention" and every count are the API's; planned starts are
 * calendar days.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { WidgetFigure, WidgetPanel } from '../WidgetPanel';
import { useWlJobsWidget } from '../overviewQueries';
import { VerticalOff, OpsBadge, OpsRow, OpsWidgetRead, dash } from '../OpsWidgetRead';
import { wlJobStatusSkin } from '../../../lib/wireline-slickline-jobs';
import { formatCalendarDate } from '../../../lib/calendar-date';
import type { WlJobs } from '../../../types/overview-operations';

function Body({ d }: { d: WlJobs }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <WidgetFigure testId="wlj-in-progress" label="In progress" value={d.counts.in_progress} />
        <WidgetFigure testId="wlj-sheets" label="Sheets to approve" value={d.counts.sheets_to_approve} alert={d.counts.sheets_to_approve > 0} />
        <WidgetFigure testId="wlj-pc" label="Pressure control" value={d.counts.pressure_control_attention} alert={d.counts.pressure_control_attention > 0} />
        <WidgetFigure testId="wlj-late" label="Late" value={d.counts.late} alert={d.counts.late > 0} />
      </div>
      {d.counts.units_without_stack > 0 && (
        <p data-testid="wlj-no-stack" className="mt-3 rounded-fi-control border border-fi-signal-amber/40 bg-fi-signal-amber/10 px-3 py-2 text-sm text-fieldiq-text">
          {`${d.counts.units_without_stack} unit${d.counts.units_without_stack === 1 ? '' : 's'} on an open job ${d.counts.units_without_stack === 1 ? 'has' : 'have'} no pressure-control stack.`}
        </p>
      )}
      {d.jobs.length === 0 ? (
        <p data-testid="wlj-empty" className="mt-4 text-sm text-fieldiq-muted">No open wireline jobs.</p>
      ) : (
        <div data-testid="wlj-jobs" className="mt-4 divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
          {d.jobs.map((j) => {
            const skin = wlJobStatusSkin(j.status);
            const units = Array.isArray(j.unit_numbers) && j.unit_numbers.length ? j.unit_numbers.join(', ') : null;
            return (
              <OpsRow key={j.id} to={j.link} onOpen={navigate} testId={`wlj-job-${j.id}`}>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-fieldiq-text">{dash(j.job_number)} · {dash(j.well_name)}</span>
                  <span className="block truncate text-fieldiq-muted">{dash(j.customer_name)} · {dash(units)} · {dash(j.supervisor_name)}</span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <span className="flex gap-1">
                    <OpsBadge fill={skin.fill}>{skin.label}</OpsBadge>
                    {typeof j.sheets_to_approve === 'number' && j.sheets_to_approve > 0 && (
                      <OpsBadge fill="bg-fi-signal-blue" testId="wlj-sheets-badge">{`${j.sheets_to_approve} sheet${j.sheets_to_approve === 1 ? '' : 's'}`}</OpsBadge>
                    )}
                    {j.pressure_control_attention && <OpsBadge fill="bg-fi-signal-amber" testId="wlj-pc-badge">Pressure control</OpsBadge>}
                    {j.unit_without_stack && <OpsBadge fill="bg-fi-signal-amber" testId="wlj-stack-badge">No stack</OpsBadge>}
                    {j.late && <OpsBadge fill="bg-fi-signal-red" testId="wlj-late-badge">Late</OpsBadge>}
                  </span>
                  <span className="tabular-nums text-fieldiq-text">{formatCalendarDate(j.planned_start_on, { month: 'short', day: 'numeric' }) ?? '—'}</span>
                </span>
              </OpsRow>
            );
          })}
        </div>
      )}
    </>
  );
}

export default function WlJobsWidget() {
  const { hasVertical } = useAuth();
  const on = hasVertical('wireline_slickline');
  const q = useWlJobsWidget(on);
  return (
    <WidgetPanel widgetKey="wl_jobs" eyebrow="Wireline & slickline" title="Wireline Jobs" link={on ? { label: 'Jobs', to: '/wireline-slickline/jobs' } : undefined}>
      <div className="p-4">
        {!on ? (
          <VerticalOff testId="wlj-vertical-off">Wireline & slickline is not on for this company.</VerticalOff>
        ) : (
          <OpsWidgetRead q={q} subject="wireline jobs" allowed="admins, office staff, supervisors, dispatchers and logistics">
            {(d) => <Body d={d} />}
          </OpsWidgetRead>
        )}
      </div>
    </WidgetPanel>
  );
}
